import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "./Dashboard";
import { Plus, Pencil, Trash2, Send, Eye, FileText, Copy, X } from "lucide-react";
import { copyToClipboardWithToast } from "@/lib/clipboard";

const PLACEHOLDER_HINTS = [
  "{{client_name}}", "{{client_email}}", "{{client_phone}}",
  "{{service}}", "{{tier}}", "{{session_date}}", "{{session_time}}",
  "{{total}}", "{{deposit}}", "{{studio_name}}", "{{contract_number}}", "{{today}}",
];

export default function AdminContracts() {
  return (
    <div className="space-y-8" data-testid="admin-contracts-page">
      <div>
        <div className="eyebrow mb-2">Contracts</div>
        <h1 className="font-serif text-4xl font-light">Templates & client agreements</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Create reusable contract templates with smart placeholders. Draft from a booking, email a signing link, and let your client e-sign in seconds.
        </p>
      </div>

      <Tabs defaultValue="contracts">
        <TabsList>
          <TabsTrigger value="contracts" data-testid="tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="contracts" className="mt-6"><ContractsPanel /></TabsContent>
        <TabsContent value="templates" className="mt-6"><TemplatesPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ContractsPanel() {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [draft, setDraft] = useState({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" });

  async function load() {
    const [c, t, b] = await Promise.all([
      api.get("/admin/contracts"),
      api.get("/admin/contract-templates"),
      api.get("/admin/bookings?status_filter=all"),
    ]);
    setContracts(c.data);
    setTemplates(t.data);
    setBookings(b.data);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!draft.template_id) return toast.error("Select a template");
    if (!draft.booking_id && (!draft.customer_name || !draft.customer_email)) {
      return toast.error("Pick a booking, or enter client name + email");
    }
    try {
      const payload = {
        template_id: draft.template_id,
        booking_id: draft.booking_id || undefined,
        title: draft.title || undefined,
        customer_name: draft.customer_name || undefined,
        customer_email: draft.customer_email || undefined,
      };
      await api.post("/admin/contracts", payload);
      toast.success("Contract created");
      setOpen(false);
      setDraft({ template_id: "", booking_id: "", title: "", customer_name: "", customer_email: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function send(id) {
    try {
      const { data } = await api.post(`/admin/contracts/${id}/send`);
      toast.success("Contract emailed to client");
      load();
      // Copy sign link to clipboard for convenience — uses the iframe-safe helper
      copyToClipboardWithToast(data.sign_url, "Sign link copied");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function voidIt(id) {
    if (!window.confirm("Void this contract?")) return;
    try {
      await api.delete(`/admin/contracts/${id}`);
      toast.success("Contract voided");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function view(c) {
    try {
      const { data } = await api.get(`/admin/contracts/${c.id}`);
      setViewing(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em] hover:bg-primary/90"
          data-testid="new-contract-btn"
        >
          <Plus className="w-4 h-4" /> New contract
        </button>
      </div>

      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-5 py-3 eyebrow font-normal">Contract</th>
              <th className="px-5 py-3 eyebrow font-normal">Client</th>
              <th className="px-5 py-3 eyebrow font-normal">Template</th>
              <th className="px-5 py-3 eyebrow font-normal">Status</th>
              <th className="px-5 py-3 eyebrow font-normal">Signed</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">No contracts yet. Click "+ New contract" to draft one.</td></tr>
            )}
            {contracts.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`contract-row-${c.contract_number}`}>
                <td className="px-5 py-4 font-mono text-xs">{c.contract_number}</td>
                <td className="px-5 py-4">
                  <div>{c.customer_name}</div>
                  <div className="text-xs text-muted-foreground">{c.customer_email}</div>
                </td>
                <td className="px-5 py-4 text-xs">{c.template_name}</td>
                <td className="px-5 py-4"><StatusBadge value={c.status} /></td>
                <td className="px-5 py-4 text-xs text-muted-foreground">{c.signed_at ? new Date(c.signed_at).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => view(c)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60" data-testid={`view-${c.contract_number}`}><Eye className="w-3.5 h-3.5" /> View</button>
                    {c.status !== "signed" && c.status !== "void" && (
                      <button onClick={() => send(c.id)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs bg-foreground text-background hover:bg-foreground/90" data-testid={`send-${c.contract_number}`}><Send className="w-3.5 h-3.5" /> Send</button>
                    )}
                    {c.status !== "signed" && (
                      <button onClick={() => voidIt(c.id)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-destructive/40 text-destructive hover:bg-destructive/5" data-testid={`void-${c.contract_number}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl font-light">New contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Template *">
              <Select value={draft.template_id} onValueChange={(v) => setDraft({ ...draft, template_id: v })}>
                <SelectTrigger className="mt-1" data-testid="contract-template-select"><SelectValue placeholder="Choose template…" /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Linked booking (auto-fills placeholders)">
              <Select value={draft.booking_id} onValueChange={(v) => setDraft({ ...draft, booking_id: v })}>
                <SelectTrigger className="mt-1" data-testid="contract-booking-select"><SelectValue placeholder="Optional — pick a booking" /></SelectTrigger>
                <SelectContent>
                  {bookings.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.reference} · {b.customer_name} · {b.service_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Or client name" value={draft.customer_name} onChange={(v) => setDraft({ ...draft, customer_name: v })} testid="contract-name" />
              <Input label="Or client email" value={draft.customer_email} onChange={(v) => setDraft({ ...draft, customer_email: v })} testid="contract-email" />
            </div>
            <Input label="Title (optional override)" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="contract-title" />
          </div>
          <DialogFooter>
            <button onClick={create} className="inline-flex items-center px-5 h-10 bg-primary text-primary-foreground text-xs uppercase tracking-[0.18em]" data-testid="contract-create-btn">Create draft</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-light">{viewing.title}</DialogTitle>
                <div className="text-xs text-muted-foreground mt-1 font-mono">{viewing.contract_number} · {viewing.status}</div>
              </DialogHeader>
              {viewing.sign_url && (
                <div className="bg-muted px-4 py-3 flex items-center gap-3 text-xs">
                  <span className="text-muted-foreground shrink-0">Sign URL:</span>
                  <code className="flex-1 truncate">{viewing.sign_url}</code>
                  <button
                    onClick={() => copyToClipboardWithToast(viewing.sign_url, "Copied")}
                    className="inline-flex items-center gap-1 px-2 h-7 border border-border hover:border-foreground/60"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              )}
              <pre className="whitespace-pre-wrap text-sm font-serif leading-relaxed max-h-[55vh] overflow-y-auto p-2">{viewing.body}</pre>
              {viewing.signed_at && (
                <div className="border-t border-border pt-4 mt-2">
                  <div className="eyebrow mb-2">Signed</div>
                  <div className="text-sm">{viewing.signature_name} · {new Date(viewing.signed_at).toLocaleString()}</div>
                  {viewing.signature_image && (
                    <img src={viewing.signature_image} alt="signature" className="mt-2 h-16 object-contain bg-white border border-border" />
                  )}
                  <div className="text-xs text-muted-foreground mt-1">IP: {viewing.signed_ip || "—"}</div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    const { data } = await api.get("/admin/contract-templates");
    setTemplates(data);
  }
  useEffect(() => { load(); }, []);

  async function save(orig, draft) {
    try {
      const payload = {
        name: draft.name,
        description: draft.description || "",
        body: draft.body,
        is_active: !!draft.is_active,
      };
      if (orig?.id) {
        await api.patch(`/admin/contract-templates/${orig.id}`, payload);
      } else {
        await api.post("/admin/contract-templates", payload);
      }
      toast.success("Saved");
      setEditing(null);
      setCreating(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  async function remove(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/contract-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-5 h-10 bg-foreground text-background text-xs uppercase tracking-[0.18em]" data-testid="new-template-btn">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-card border border-border p-5" data-testid={`template-${t.id}`}>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-xl">{t.name}</div>
                {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
                <div className="text-xs text-muted-foreground mt-3 line-clamp-3 font-serif">{t.body}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setEditing(t)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-border hover:border-foreground/60" data-testid={`edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => remove(t)} className="inline-flex items-center gap-1.5 px-3 h-8 text-xs border border-destructive/40 text-destructive hover:bg-destructive/5" data-testid={`delete-template-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      <TemplateDialog
        open={!!editing || creating}
        onClose={() => { setEditing(null); setCreating(false); }}
        original={editing}
        onSave={(draft) => save(editing, draft)}
      />
    </div>
  );
}

function TemplateDialog({ open, onClose, original, onSave }) {
  const [draft, setDraft] = useState({ name: "", description: "", body: "", is_active: true });

  useEffect(() => {
    if (open) {
      setDraft(original ? { ...original } : { name: "", description: "", body: "", is_active: true });
    }
  }, [open, original]);

  function insertPlaceholder(p) {
    setDraft((d) => ({ ...d, body: (d.body || "") + p }));
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-light">{original ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input label="Name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="template-name" />
          <Input label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} testid="template-description" />
          <div>
            <span className="eyebrow">Body</span>
            <textarea
              rows={18}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="mt-1 w-full bg-transparent border border-border p-3 outline-none focus:border-foreground text-sm font-serif leading-relaxed"
              data-testid="template-body"
              placeholder="PHOTOGRAPHY SESSION AGREEMENT&#10;&#10;This agreement is made between {{studio_name}} and {{client_name}}..."
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mr-2 self-center">Insert:</span>
              {PLACEHOLDER_HINTS.map((p) => (
                <button key={p} type="button" onClick={() => insertPlaceholder(p)} className="px-2 py-0.5 text-[11px] font-mono bg-muted hover:bg-accent border border-border">
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="px-5 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60">Cancel</button>
          <button onClick={() => onSave(draft)} className="px-5 h-10 text-xs uppercase tracking-[0.18em] bg-primary text-primary-foreground hover:bg-primary/90" data-testid="template-save-btn">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm" data-testid={testid} />
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      {children}
    </label>
  );
}
